import React, { useMemo } from 'react'

type Context = {
  /**
   * The `z-index` of the parent element.
   */
  parentZIndex: number
}

const Context = React.createContext<Context | null>(null)

/**
 * Global context provider that manages the `z-index` of the modal elements.
 */
export const ZStackProvider = ({ children }: React.PropsWithChildren) => {
  const init = useMemo(() => {
    const val: Context = { parentZIndex: 1 }
    return val
  }, [])

  return <Context.Provider value={init}>{children}</Context.Provider>
}

const STEP_SIZE = 100

/**
 * Lets you create an element that is going to be stacked on top of the other elements.
 *
 * @example
 *
 * ```tsx
 * <ZStack>
 *   {({ zIndex }) => (
 *     <div style={{ zIndex }}>hi</div>
 *   )}
 * </ZStack>
 * ```
 */
export const ZStack = ({ children }: { children: (props: { zIndex: number }) => React.ReactNode }) => {
  const parentCtx = React.useContext(Context)

  if (parentCtx == null) {
    throw new Error('Missing ZLayoutContextProvider')
  }

  const elementZIndex = useMemo(() => {
    return parentCtx.parentZIndex + STEP_SIZE
  }, [parentCtx.parentZIndex])

  const Component = useMemo(() => children({ zIndex: elementZIndex }), [children, elementZIndex])

  const elementCtx = useMemo(() => {
    const newVal: Context = { parentZIndex: elementZIndex }
    return newVal
  }, [elementZIndex])

  return <Context.Provider value={elementCtx}>{Component}</Context.Provider>
}
